import { useMemo, useState, type ReactNode } from 'react';
import { StatusBadge, Tooltip } from '@/components/UI';
import {
    formatMetricValue,
    percentToneClass,
    RANDOM_EDGE_BASIS_LABELS,
    type IdeaLabRunCell,
    type IdeaLabRunReport,
    type ReportMetric
} from '../ideaLabUtils';
import {
    buildScoreboard,
    formatPercentagePoints,
    sortScoreboardRows,
    type ReportView,
    type ScoreboardColumn,
    type ScoreboardColumnKey,
    type ScoreboardRow,
    type ScoreboardSort
} from '../reportView';

interface RunScoreboardProps {
    report: Pick<IdeaLabRunReport, 'cells' | 'horizons' | 'variants'>;
    view: Pick<ReportView, 'entityType' | 'lens' | 'windowRole' | 'horizonSeconds'>;
    /** Highlights this variant's row, e.g. the one whose detail shows below. */
    selectedVariantId?: number | null;
    onRowClick: (variantId: number) => void;
    /** Accessible description of what a row click does. */
    rowActionLabel: string;
}

const formatColumnValue = (key: ScoreboardColumnKey, value: number | null): string =>
    key === 'meanVsControl' ? formatPercentagePoints(value) : formatMetricValue(key satisfies ReportMetric, value);

const cellDetail = (key: ScoreboardColumnKey, cell: IdeaLabRunCell): ReactNode => {
    switch (key) {
        case 'supportCount':
            return `${cell.distinctEntityCount} symbols · ${cell.distinctDateCount} dates · ${cell.immatureCount} immature · ${cell.missingCount} missing`;
        case 'randomEdgeMeanPercent':
            return `${cell.randomEdgeCoveredCount} of ${cell.supportCount} priced entries had a ${RANDOM_EDGE_BASIS_LABELS[cell.randomEdgeBasis]}`;
        case 'tradabilityRate':
            return `${cell.option.noContractCount} had no matching contract · ${cell.option.notEnteredCount} could not be entered`;
        default:
            return null;
    }
};

/**
 * Every variant of one report slice with its metrics side by side, the difference from the run's
 * control, and the highest value per column marked. Column headers sort; clicking a row hands the
 * variant to the caller.
 */
const RunScoreboard = ({ report, view, selectedVariantId = null, onRowClick, rowActionLabel }: RunScoreboardProps) => {
    const [sort, setSort] = useState<ScoreboardSort | null>(null);
    const scoreboard = useMemo(() => buildScoreboard(report, view), [report, view]);
    const rows = useMemo(() => sortScoreboardRows(scoreboard.rows, sort), [scoreboard.rows, sort]);
    const parentKeys = useMemo(() => new Map(report.variants.map((variant) => [variant.variantId, variant.variantKey])), [report.variants]);

    const toggleSort = (key: ScoreboardColumnKey) =>
        setSort((current) =>
            current?.key !== key ? { key, direction: 'desc' } : current.direction === 'desc' ? { key, direction: 'asc' } : null
        );

    const renderValue = (row: ScoreboardRow, { key, signed }: ScoreboardColumn) => {
        const value = row.values[key];
        const isBest = value !== null && scoreboard.best[key] === value;
        const detail = row.cell === null ? null : cellDetail(key, row.cell);
        const text = formatColumnValue(key, value);
        const content = (
            <span
                className={`inline-flex items-baseline gap-1 rounded px-1.5 py-0.5 tabular-nums ${
                    isBest ? 'bg-emerald-500/15 font-semibold ring-1 ring-emerald-500/40' : ''
                } ${signed ? percentToneClass(value) : 'text-zinc-200'}`}
            >
                {text}
                {key === 'randomEdgeMeanPercent' && row.cell !== null && (
                    <span className="text-[11px] font-normal text-zinc-500">
                        {row.cell.randomEdgeCoveredCount}/{row.cell.supportCount}
                    </span>
                )}
            </span>
        );

        if (detail === null && !isBest) return content;

        return <Tooltip content={[isBest ? 'Best in this column.' : null, detail].filter(Boolean).join('\n')}>{content}</Tooltip>;
    };

    return (
        <div className="overflow-x-auto rounded-md border border-zinc-800">
            <table className="w-full min-w-[760px] text-sm">
                <thead className="bg-[#131416]">
                    <tr className="text-left text-[11px] uppercase tracking-wide text-zinc-400">
                        <th className="px-3 py-2 font-medium">Variant</th>
                        {scoreboard.columns.map((column) => {
                            const sorted = sort?.key === column.key ? sort.direction : null;

                            return (
                                <th
                                    aria-sort={sorted === null ? 'none' : sorted === 'desc' ? 'descending' : 'ascending'}
                                    className="px-2 py-2 text-right font-medium"
                                    key={column.key}
                                >
                                    <button
                                        className="inline-flex items-center gap-1 uppercase tracking-wide hover:text-zinc-200"
                                        title={`${column.description}. Click to sort.`}
                                        type="button"
                                        onClick={() => toggleSort(column.key)}
                                    >
                                        {column.label}
                                        <span aria-hidden="true" className="w-2 text-zinc-500">
                                            {sorted === 'desc' ? '↓' : sorted === 'asc' ? '↑' : ''}
                                        </span>
                                    </button>
                                </th>
                            );
                        })}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => {
                        const { variant } = row;
                        const isSelected = variant.variantId === selectedVariantId;
                        const parentKey = variant.parentVariantId === null ? null : (parentKeys.get(variant.parentVariantId) ?? null);

                        return (
                            <tr
                                aria-selected={isSelected}
                                className={`cursor-pointer border-t border-zinc-800 ${isSelected ? 'bg-blue-500/10' : 'hover:bg-zinc-800/40'}`}
                                data-variant-id={variant.variantId}
                                key={variant.variantId}
                                onClick={() => onRowClick(variant.variantId)}
                            >
                                <td
                                    className={`max-w-[26rem] border-l-2 px-3 py-2 align-top ${isSelected ? 'border-blue-400' : 'border-transparent'}`}
                                >
                                    <button
                                        aria-label={`${rowActionLabel}: ${variant.label}`}
                                        className="text-left font-medium text-zinc-100 hover:text-white focus-visible:rounded focus-visible:outline-2 focus-visible:outline-blue-400"
                                        type="button"
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            onRowClick(variant.variantId);
                                        }}
                                    >
                                        {variant.label}
                                    </button>
                                    <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-zinc-500">
                                        <span>
                                            {variant.variantKey} · #{variant.variantId}
                                        </span>
                                        {variant.role === 'control' && (
                                            <StatusBadge status="control" tone="accent">
                                                Control
                                            </StatusBadge>
                                        )}
                                        {parentKey !== null && <span>child of {parentKey}</span>}
                                    </div>
                                </td>
                                {row.cell === null ? (
                                    <td className="px-3 py-2 text-right text-xs text-zinc-500" colSpan={scoreboard.columns.length}>
                                        No entries in this slice
                                    </td>
                                ) : (
                                    scoreboard.columns.map((column) => (
                                        <td className="px-2 py-2 text-right align-top" key={column.key}>
                                            {column.key === 'meanVsControl' && variant.role === 'control' ? (
                                                <span className="text-xs text-zinc-500">baseline</span>
                                            ) : (
                                                renderValue(row, column)
                                            )}
                                        </td>
                                    ))
                                )}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default RunScoreboard;
